import type { SsoConnectionStatus } from '../../../services/api';
import {
    ADMIN_BADGE_POSITIVE_CLASS,
    ADMIN_BADGE_NEGATIVE_CLASS,
    ADMIN_BADGE_WARNING_CLASS,
} from './adminStatusBadge';

export interface CompanySsoDetailStateInput {
    companyId: string;
    auth0OrganizationId: string | null | undefined;
    connectionStatus: SsoConnectionStatus | null;
    hasConnectionStatusError?: boolean;
    isLoadingConnectionStatus: boolean;
}

export interface CompanySsoDetailState {
    badgeLabel: string;
    badgeClassName: string;
    cardValue: string;
    cardDetail: string;
    manageSsoDisabled: boolean;
    manageSsoPath: string | null;
    manageSsoTooltip: string | null;
}

export function getCompanySsoDetailState({
    auth0OrganizationId,
    companyId,
    connectionStatus,
    hasConnectionStatusError = false,
    isLoadingConnectionStatus,
}: CompanySsoDetailStateInput): CompanySsoDetailState {
    const hasAuth0Organization = Boolean(auth0OrganizationId?.trim());
    const manageSsoPath = hasAuth0Organization ? `/admin/companies/${companyId}/sso` : null;

    if (!hasAuth0Organization) {
        return {
            badgeLabel: 'SSO unavailable',
            badgeClassName: 'bg-neutral-100 text-neutral-600 ring-1 ring-inset ring-neutral-300',
            cardValue: 'Sync Auth0 first',
            cardDetail: 'Sync this company to Auth0 before managing SSO.',
            manageSsoDisabled: true,
            manageSsoPath,
            manageSsoTooltip: 'Sync this company to Auth0 before managing SSO.',
        };
    }

    if (isLoadingConnectionStatus) {
        return {
            badgeLabel: 'Checking SSO',
            badgeClassName: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/25',
            cardValue: 'Checking…',
            cardDetail: 'Loading Auth0 SSO connection status.',
            manageSsoDisabled: false,
            manageSsoPath,
            manageSsoTooltip: null,
        };
    }

    if (hasConnectionStatusError) {
        return {
            badgeLabel: 'SSO status unavailable',
            badgeClassName: ADMIN_BADGE_NEGATIVE_CLASS,
            cardValue: 'Unable to check',
            cardDetail: 'Could not load Auth0 SSO connection status.',
            manageSsoDisabled: false,
            manageSsoPath,
            manageSsoTooltip: null,
        };
    }

    if (connectionStatus?.exists) {
        const strategy = connectionStatus.connection?.strategy?.trim().toUpperCase();
        return {
            badgeLabel: 'SSO configured',
            badgeClassName: ADMIN_BADGE_POSITIVE_CLASS,
            cardValue: 'Configured',
            cardDetail: [connectionStatus.connectionName, strategy].filter(Boolean).join(' · '),
            manageSsoDisabled: false,
            manageSsoPath,
            manageSsoTooltip: null,
        };
    }

    return {
        badgeLabel: 'SSO not configured',
        badgeClassName: ADMIN_BADGE_WARNING_CLASS,
        cardValue: 'Not configured',
        cardDetail: 'No Auth0 SSO connection has been configured yet.',
        manageSsoDisabled: false,
        manageSsoPath,
        manageSsoTooltip: null,
    };
}
